/**
 * Leviathan Web - Appearance Settings
 * Quasar Prime LLC
 *
 * Lets the user pick a color palette, font size, and display options.
 * All changes apply immediately and persist automatically via ThemeContext.
 */

import { Check, Palette as PaletteIcon, Type, SlidersHorizontal } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { palettes, fontSizes, Palette } from '@/styles/palettes';

const FONT_SIZE_OPTIONS: { key: keyof typeof fontSizes; label: string }[] = [
  { key: 'small', label: 'Small' },
  { key: 'medium', label: 'Medium' },
  { key: 'large', label: 'Large' },
  { key: 'xlarge', label: 'Extra Large' },
];

/** Decorative corner brackets, highlighted when a card is selected. */
function CornerBrackets({ active }: { active: boolean }) {
  const color = active ? 'var(--primary)' : 'rgba(var(--border-strong-rgb) / 0.8)';
  return (
    <>
      <span
        className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2"
        style={{ borderColor: color }}
      />
      <span
        className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2"
        style={{ borderColor: color }}
      />
      <span
        className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2"
        style={{ borderColor: color }}
      />
      <span
        className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2"
        style={{ borderColor: color }}
      />
    </>
  );
}

/** Mini live preview of a palette using that palette's own colors. */
function PalettePreview({ palette }: { palette: Palette }) {
  const c = palette.colors;
  return (
    <div
      className="rounded-sm overflow-hidden border"
      style={{ background: c.bgBase, borderColor: c.borderDefault }}
    >
      {/* Header bar in primary */}
      <div className="h-5 w-full" style={{ background: c.primary }} />
      <div className="p-3 space-y-2">
        {/* A faux panel */}
        <div
          className="h-8 w-full rounded-sm border flex items-center px-2 gap-1.5"
          style={{ background: c.bgPanel, borderColor: c.borderDefault }}
        >
          <span
            className="h-2.5 w-2.5 rounded-full"
            style={{ background: c.statusActive }}
          />
          <span
            className="h-2.5 w-2.5 rounded-full"
            style={{ background: c.statusWarning }}
          />
          <span
            className="h-2.5 w-2.5 rounded-full"
            style={{ background: c.statusError }}
          />
          <span
            className="h-2 flex-1 rounded-full ml-1"
            style={{ background: c.borderStrong }}
          />
        </div>
        {/* Text lines */}
        <div className="space-y-1">
          <div
            className="h-2 w-3/4 rounded-full"
            style={{ background: c.textSecondary }}
          />
          <div
            className="h-2 w-1/2 rounded-full"
            style={{ background: c.textTertiary }}
          />
        </div>
      </div>
    </div>
  );
}

/** Tactical toggle switch. */
function Toggle({
  checked,
  onChange,
  label,
  description,
}: {
  checked: boolean;
  onChange: (value: boolean) => void;
  label: string;
  description: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className="w-full flex items-center justify-between gap-4 bg-base-dark border border-panel-border hover:border-amber/50 px-4 py-3 transition-all text-left"
    >
      <span>
        <span className="block font-mono text-sm uppercase tracking-wider text-text-primary">
          {label}
        </span>
        <span className="block text-xs text-text-secondary mt-0.5">
          {description}
        </span>
      </span>
      <span
        className={`relative shrink-0 w-12 h-6 rounded-full transition-colors ${
          checked ? 'bg-amber' : 'bg-panel-border'
        }`}
      >
        <span
          className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-base-dark transition-transform ${
            checked ? 'translate-x-6' : 'translate-x-0'
          }`}
        />
      </span>
    </button>
  );
}

export default function AppearanceSettingsPage() {
  const { settings, updateSettings } = useTheme();

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-8">
      {/* Header */}
      <div className="section-header flex items-center gap-3">
        <PaletteIcon className="w-6 h-6 text-amber" />
        <div>
          <h1
            className="text-2xl font-display font-bold text-amber uppercase tracking-wide"
            style={{ textShadow: '0 0 10px var(--primary-glow)' }}
          >
            Appearance
          </h1>
          <p className="text-text-secondary text-sm font-mono">
            Personalize your command center. Changes apply &amp; save instantly.
          </p>
        </div>
      </div>

      {/* PALETTE SELECTOR */}
      <section className="bg-panel-dark clip-path-octagon p-6 relative">
        <div className="flex items-center gap-2 mb-4">
          <PaletteIcon className="w-4 h-4 text-amber" />
          <h2 className="font-mono text-xs uppercase tracking-[0.15em] text-text-secondary">
            Color Palette
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(palettes).map(([key, palette]) => {
            const active = settings.palette === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => updateSettings({ palette: key })}
                className="relative text-left p-3 transition-all bg-base-dark"
                style={{
                  border: '1px solid',
                  borderColor: active ? 'var(--primary)' : 'var(--border-default)',
                  boxShadow: active ? '0 0 16px var(--primary-glow)' : 'none',
                }}
              >
                <CornerBrackets active={active} />

                <div className="flex items-center justify-between mb-2">
                  <span className="font-display font-bold text-sm text-text-primary uppercase tracking-wide">
                    {palette.name}
                  </span>
                  {active && (
                    <span
                      className="flex items-center justify-center w-5 h-5 rounded-full"
                      style={{ background: 'var(--primary)' }}
                    >
                      <Check className="w-3 h-3 text-base-dark" strokeWidth={3} />
                    </span>
                  )}
                </div>

                <PalettePreview palette={palette} />

                <p className="text-[11px] text-text-tertiary font-mono mt-2">
                  {palette.description}
                </p>
                <span className="inline-block mt-1 text-[10px] font-mono uppercase tracking-wider text-text-tertiary">
                  {palette.mode === 'light' ? '☀ Light' : '◐ Dark'}
                </span>
              </button>
            );
          })}
        </div>
      </section>

      {/* FONT SIZE */}
      <section className="bg-panel-dark clip-path-octagon p-6 relative">
        <div className="flex items-center gap-2 mb-4">
          <Type className="w-4 h-4 text-amber" />
          <h2 className="font-mono text-xs uppercase tracking-[0.15em] text-text-secondary">
            Font Size
          </h2>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-5">
          {FONT_SIZE_OPTIONS.map((opt) => {
            const active = settings.fontSize === opt.key;
            return (
              <button
                key={opt.key}
                type="button"
                onClick={() => updateSettings({ fontSize: opt.key })}
                className={`py-3 font-mono text-xs uppercase tracking-wider border transition-all ${
                  active
                    ? 'bg-amber text-base-dark border-amber'
                    : 'bg-base-dark text-text-secondary border-panel-border hover:border-amber/50 hover:text-text-primary'
                }`}
              >
                {opt.label}
                <span className="block mt-1 opacity-70 normal-case tracking-normal">
                  {fontSizes[opt.key]}
                </span>
              </button>
            );
          })}
        </div>

        <div className="bg-base-dark border border-panel-border p-4">
          <p className="text-[10px] font-mono uppercase tracking-widest text-text-tertiary mb-2">
            Preview
          </p>
          <p
            className="text-text-primary"
            style={{ fontSize: fontSizes[settings.fontSize as keyof typeof fontSizes] }}
          >
            The quick brown fox jumps over the lazy dog
          </p>
        </div>
      </section>

      {/* DISPLAY OPTIONS */}
      <section className="bg-panel-dark clip-path-octagon p-6 relative">
        <div className="flex items-center gap-2 mb-4">
          <SlidersHorizontal className="w-4 h-4 text-amber" />
          <h2 className="font-mono text-xs uppercase tracking-[0.15em] text-text-secondary">
            Display Options
          </h2>
        </div>

        <div className="space-y-3">
          <Toggle
            checked={settings.compactMode}
            onChange={(v) => updateSettings({ compactMode: v })}
            label="Compact Mode"
            description="Reduce spacing for more data density"
          />
          <Toggle
            checked={settings.reduceMotion}
            onChange={(v) => updateSettings({ reduceMotion: v })}
            label="Reduce Motion"
            description="Disable animations and transitions"
          />
          <Toggle
            checked={settings.sidebarCollapsed}
            onChange={(v) => updateSettings({ sidebarCollapsed: v })}
            label="Sidebar Collapsed"
            description="Start with the sidebar collapsed by default"
          />
        </div>
      </section>
    </div>
  );
}
